import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// Types for the new research system
export interface ResearchData {
  overview: OverviewData;
  demographics: DemographicsData;
  competitor: CompetitorData;
  reviews: ReviewsData;
  discovery: DiscoveryData;
  keywords: KeywordsData;
}

export interface OverviewData {
  top_suggestions: SuggestionItem[];
  strengths: StrengthWeaknessItem[];
  weaknesses: StrengthWeaknessItem[];
  buying_factors: BuyingFactorItem[];
  keywords: KeywordItem[];
}

export interface SuggestionItem {
  title: string;
  description: string;
  impact_percentage: number;
  impact_level: 'High' | 'Medium' | 'Low';
  tags: string[];
  reasoning: string;
  priority: number;
}

export interface StrengthWeaknessItem {
  feature: string;
  percentage: number;
  description: string;
  evidence: string;
  category: 'strength' | 'weakness';
}

export interface BuyingFactorItem {
  factor: string;
  percentage: number;
  impact: string;
  color: 'green' | 'yellow' | 'red';
}

export interface KeywordItem {
  keyword: string;
  placement: string;
  action: 'replace' | 'add';
  reasoning: string;
  search_volume?: number;
  competition?: string;
}

export interface DemographicsData {
  gender_distribution: Record<string, number>;
  age_distribution: Record<string, number>;
  geographic_distribution: Record<string, number>;
  usage_scenarios: Array<{ scenario: string; percentage: number; context: string; key_reason: string }>;
  purchase_drivers: Array<{ trigger_event: string; core_motivation: string; driver_type: string; percentage: number }>;
  concerns_objections: Array<{ concern: string; reason: string; percentage: number }>;
  core_values: Array<{ value: string; importance: string; aligned_icp: string; percentage: number }>;
  purchase_behavior: any[];
  customer_psychology: any[];
}

export interface CompetitorData {
  direct_competitors: any[];
  indirect_competitors: any[];
  market_positioning: any;
  strengths_weaknesses: Record<string, string[]>;
  pricing_strategies: any[];
}

export interface ReviewsData {
  total_reviews: number;
  average_rating: number;
  positive_reviews: any[];
  negative_reviews: any[];
  sentiment_analysis: any;
  common_themes: any[];
}

export interface DiscoveryData {
  search_patterns: any[];
  discovery_channels: any[];
  conversion_optimization: any[];
  market_trends: any[];
}

export interface KeywordsData {
  primary_keywords: KeywordItem[];
  long_tail_keywords: KeywordItem[];
  competitor_keywords: KeywordItem[];
  search_volume_data: Record<string, number>;
  ranking_opportunities: any[];
}

export interface ResearchDashboardResponse {
  project: {
    id: number;
    project_name: string;
    brand_name: string;
    product_info: string;
    amazon_listing_link: string;
    website_link: string;
    instagram_link: string;
    current_product_info: string;
    listing_elements: string[];
    product_category: string[];
    category_other: string;
    not_working: string[];
    not_working_other: string;
    has_assets: boolean;
    white_background_link: string;
    old_images_link: string;
    lifestyle_image_link: string;
    user_generated_content_link: string;
    video_content_link: string;
    no_assets_reason?: string;
    created_at: string;
  };
  reports: Record<string, {
    id: number;
    type: string;
    content: any;
    status: string;
    created_at: string;
  }>;
  scraped_data: Array<any> | Record<string, any[]>;
  last_updated: string;
}

export interface ScrapedDataRequest {
  project_id: number;
  data_type: string;
  raw_data: any;
  source: string;
  metadata?: any;
}

export interface ResearchGenerationRequest {
  project_id: number;
  report_types: string[];
  background?: boolean;
}

// Onboarding form types
export interface OnboardingFormData {
  project_name: string;
  brand_name: string;
  product_info: string;
  amazon_listing_link: string;
  website_link: string;
  instagram_link: string;
  current_product_info: string;
  listing_elements: string[];
  product_category: string[];
  category_other: string;
  not_working: string[];
  not_working_other: string;
  has_assets: boolean;
  white_background_link: string;
  old_images_link: string;
  lifestyle_image_link: string;
  user_generated_content_link: string;
  video_content_link: string;
  no_assets_reason: string;
}

export interface OnboardingFormResponse {
  success?: boolean;
  status?: string;
  message?: string;
  project_id?: number;
  task_id?: string;
}

import { API_BASE_URL } from '../config/api';

// API functions
const api = {
  async getResearchDashboard(projectId: number): Promise<ResearchDashboardResponse> {
    console.log('🔍 Fetching research dashboard for project:', projectId);
    console.log('🔗 API URL:', `${API_BASE_URL}/api/research-dashboard/${projectId}`);
    
    const response = await fetch(`${API_BASE_URL}/api/research-dashboard/${projectId}`);
    console.log('📡 Response status:', response.status);
    
    if (!response.ok) {
      console.error('❌ API Error:', response.status, response.statusText);
      throw new Error('Failed to fetch research dashboard');
    }
    
    const data = await response.json();
    console.log('✅ API Response received:', data);
    return data;
  },

  async storeScrapedData(data: ScrapedDataRequest): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/api/store-scraped-data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error('Failed to store scraped data');
    }
    return response.json();
  },

  async generateResearchReport(projectId: number, reportType: string, forceRegenerate = false): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/api/generate-research-report`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        project_id: projectId,
        report_type: reportType,
        force_regenerate: forceRegenerate,
      }),
    });
    if (!response.ok) {
      throw new Error('Failed to generate research report');
    }
    return response.json();
  },

  async generateResearch(request: ResearchGenerationRequest): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/api/generate-research`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      throw new Error('Failed to generate research');
    }
    return response.json();
  },
  
  async generateOnboardingForm(data: OnboardingFormData): Promise<OnboardingFormResponse> {
    const response = await fetch(`${API_BASE_URL}/api/generate-onboarding-form`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error('Failed to generate onboarding form');
    }
    return response.json();
  },
};

// React Query hooks
export const useResearchDashboard = (projectId: number | null, options?: { disablePolling?: boolean }) => {
  console.log('🔍 useResearchDashboard called with projectId:', projectId, 'Type:', typeof projectId);
  console.log('🔍 Query enabled:', !!projectId);
  console.log('🔍 Disable polling:', options?.disablePolling);
  
  return useQuery({
    queryKey: ['research-dashboard', projectId],
    queryFn: () => api.getResearchDashboard(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: options?.disablePolling ? false : (data) => {
      // Stop polling if creative brief is ready OR if user is active
      const hasBrief = !!(data as any)?.creative_brief?.brief_json;
      console.log('🔄 Polling check - hasBrief:', hasBrief, 'disablePolling:', options?.disablePolling);
      
      // If user is active or brief is ready, stop polling
      if (options?.disablePolling || hasBrief) {
        console.log('🛑 Stopping polling - user active or brief ready');
        return false;
      }
      
      console.log('🔄 Continuing to poll every 5 seconds');
      return 5 * 1000; // Poll every 5 seconds until brief is ready
    },
  });
};

export const useStoreScrapedData = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: api.storeScrapedData,
    onSuccess: (data, variables) => {
      void data;
      // Invalidate and refetch research dashboard
      queryClient.invalidateQueries({
        queryKey: ['research-dashboard', variables.project_id],
      });
    },
  });
};

export const useGenerateResearchReport = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ projectId, reportType, forceRegenerate }: {
      projectId: number;
      reportType: string;
      forceRegenerate?: boolean;
    }) => api.generateResearchReport(projectId, reportType, forceRegenerate),
    onSuccess: (data, variables) => {
      void data;
      // Invalidate and refetch research dashboard
      queryClient.invalidateQueries({
        queryKey: ['research-dashboard', variables.projectId],
      });
    },
  });
};

export const useGenerateResearch = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: api.generateResearch,
    onSuccess: (data, variables) => {
      void data;
      // Invalidate and refetch research dashboard
      queryClient.invalidateQueries({
        queryKey: ['research-dashboard', variables.project_id],
      });
    },
  });
};

export const useGenerateOnboardingForm = () => {
  return useMutation({
    mutationFn: api.generateOnboardingForm,
  });
};

// Legacy hooks for backward compatibility
export const useDashboardData = useResearchDashboard;
export const useGenerateContent = useGenerateResearch;

// Creative Brief specific hooks
export const useRegenerateSection = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ project_id, section_name, field_key, feedback, current_content }: {
      project_id: string;
      section_name: string;
      field_key?: string;
      feedback?: string;
      current_content?: string;
    }) => {
      // Use background regeneration to receive a task_id for UI progress
      const response = await fetch(`${API_BASE_URL}/api/regenerate-section`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project_id,
          section_name,
          field_key,
          feedback,
          current_content
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to regenerate section');
      }
      
      return response.json();
    },
    onSuccess: (data, variables) => {
      void data;
      // Invalidate and refetch dashboard data
      queryClient.invalidateQueries({
        queryKey: ['research-dashboard', variables.project_id],
      });
    },
  });
};

export const useTaskStatus = (taskId: string | null, options?: any) => {
  return useQuery({
    queryKey: ['task-status', taskId],
    queryFn: async () => {
      if (!taskId) return null;
      
      console.log('🔧 DEBUG: Fetching task status for task ID:', taskId);
      const response = await fetch(`${API_BASE_URL}/api/task-status/${taskId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch task status');
      }
      const data = await response.json();
      console.log('🔧 DEBUG: Task status response:', data);
      return data;
    },
    enabled: !!taskId,
    refetchInterval: options?.refetchInterval || 2000,
    ...options
  });
};

export const useCreativeBriefHistory = (projectId: string | null, sectionName?: string) => {
  return useQuery({
    queryKey: ['creative-brief-history', projectId, sectionName],
    queryFn: async () => {
      if (!projectId) return null;
      
      let url = `${API_BASE_URL}/api/creative-brief-history/${projectId}`;
      if (sectionName) {
        url += `?section_name=${encodeURIComponent(sectionName)}`;
      }
      
      console.log('🔧 DEBUG: Fetching history from URL:', url);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch creative brief history');
      }
      const data = await response.json();
      console.log('🔧 DEBUG: History response:', data);
      return data;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useSaveCreativeBrief = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ project_id, brief_data }: {
      project_id: string;
      brief_data: Record<string, any>;
    }) => {
      const response = await fetch(`${API_BASE_URL}/api/save-creative-brief`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project_id,
          brief_data
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to save creative brief');
      }
      
      return response.json();
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch the research dashboard data
      queryClient.invalidateQueries({ queryKey: ['research-dashboard', variables.project_id] });
      console.log('🔧 DEBUG: Creative brief saved successfully:', data);
    },
    onError: (error, variables) => {
      console.error('🔧 DEBUG: Failed to save creative brief (optimistic cache fallback):', error);
      try {
        // Optimistically persist the brief into the dashboard cache so UI/PDF have latest data
        queryClient.setQueryData(['research-dashboard', variables.project_id], (prev: any) => {
          const draft = prev ? { ...prev } : {} as any;
          if (!draft.creative_brief) draft.creative_brief = {} as any;
          draft.creative_brief.brief_json = variables.brief_data;
          return draft;
        });
      } catch (e) {
        console.error('🔧 DEBUG: Failed to write optimistic cache:', e);
      }
    }
  });
}; 